import { MACRONUTRIENT_STATUS_LABELS } from '../constants/maintenance.constants';
import type {
	FertilizationPlanAdvice,
	MaintenanceMixture,
	MaintenanceMixtureProduct,
	MaintenanceMixtureUsage,
	MaintenanceSectorFertilization,
} from '../types';
import { toCoverage } from './coverage';
import { getMaintenanceMonths, getMaintenanceReferenceName } from './maintenanceAdviceMapper';
import type { PlanningTableData } from './planningTableMapper';

type ProductCatalog = Array<{ _id: string; name?: string; commercialName?: string }>;

export interface MixtureUsage {
	massKg: number;
	massLimitKg: number;
	concentration: number;
	cemMax: number;
	percentage: number;
}

export interface MixtureMonthSummary {
	areaName: string;
	sectorNames: string[];
	vbox: number | null;
	applications: number;
}

const decimalFormatter = new Intl.NumberFormat('pt-BR', {
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

const doseFormatter = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 });

export const formatMixtureDecimal = (value: number) => decimalFormatter.format(value);

export const formatMixtureDose = (product: Pick<MaintenanceMixtureProduct, 'dose' | 'unit'>) =>
	`${doseFormatter.format(product.dose)} ${product.unit}`;

export const formatMixtureDate = (date: string) => {
	const [year, month, day] = date.slice(0, 10).split('-');
	return year && month && day ? `${day}/${month}/${year}` : date;
};

const MASS_TO_KG: Partial<Record<MaintenanceMixtureProduct['unit'], number>> = { kg: 1, g: 0.001 };

const monthOf = (value: string) => value.replace(/-/g, '/').slice(0, 7);

const getIrrigationRecommendations = (advice: FertilizationPlanAdvice) =>
	(advice.generatedRecommendations ?? []).filter((item) => item.method === 'irrigation');

export const getPlanningMethod = (advice: FertilizationPlanAdvice) => {
	const generated = advice.generatedRecommendations ?? [];
	if (!generated.length) return null;
	return generated.some((item) => item.method === 'irrigation') ? 'irrigation' : 'broad';
};

export const areMixturesConfirmed = (mixtures: MaintenanceMixture[] = []) =>
	mixtures.length > 0 && mixtures.every((mixture) => mixture.status === 'confirmed');

export const getMixtureMonths = (advice: FertilizationPlanAdvice) =>
	getMaintenanceMonths([
		...getIrrigationRecommendations(advice).flatMap((item) => item.periods ?? []),
		...(advice.mixtures ?? []).map((mixture) => mixture.date),
	]);

export const getMonthMixtures = (mixtures: MaintenanceMixture[] = [], month: string) =>
	mixtures
		.filter((mixture) => monthOf(mixture.date) === month)
		.sort((a, b) => a.date.localeCompare(b.date));

export const fromBackendUsage = (usage: MaintenanceMixtureUsage): MixtureUsage => ({
	massKg: usage.massKg,
	massLimitKg: usage.massLimitKg,
	concentration: usage.cem,
	cemMax: usage.cemMax,
	percentage: usage.percentage,
});

export const highestUsage = (usages: MaintenanceMixtureUsage[]) =>
	usages.reduce((highest, usage) => (usage.percentage > highest.percentage ? usage : highest));

export const toEmptyUsage = (massLimitKg: number, cemMax: number): MixtureUsage => ({
	massKg: 0,
	massLimitKg,
	concentration: 0,
	cemMax,
	percentage: 0,
});

export const mapMixtureMonthSummary = (
	advice: FertilizationPlanAdvice,
	month: string
): MixtureMonthSummary => ({
	areaName: getMaintenanceReferenceName(advice.area, advice.areaName),
	sectorNames: (advice.sectors ?? [])
		.map((sector) => getMaintenanceReferenceName(sector))
		.filter(Boolean),
	vbox: advice.fractionationSettings?.vbox ?? null,
	applications: new Set(
		getMonthMixtures(advice.mixtures, month).map((mixture) => mixture.date.slice(0, 10))
	).size,
});

export const mapMixtureNutrientDemand = (
	advice: FertilizationPlanAdvice,
	month: string
): PlanningTableData => {
	const balances = (advice.nutrientBalance ?? []).filter(
		(balance) => monthOf(balance.period) === month
	);
	const sum = (pick: (balance: (typeof balances)[number]) => number[] | undefined, index: number) =>
		balances.reduce((total, balance) => total + Number(pick(balance)?.[index] ?? 0), 0);

	return {
		columns: [
			{ key: 'nutrient', label: 'NUTRIENTE', emphasis: 'strong' },
			{ key: 'demand', label: 'DEMANDA (KG/HA)', align: 'right' },
			{ key: 'coverage', label: 'DEMANDA ATENDIDA', variant: 'coverage' },
		],
		rows: MACRONUTRIENT_STATUS_LABELS.map(({ key, label }, index) => {
			const demand = sum((balance) => balance.demand, index);
			const coverage = toCoverage(
				sum((balance) => balance.applied, index),
				demand
			);
			return {
				id: key,
				cells: { nutrient: label, demand: formatMixtureDecimal(demand) },
				coverage: coverage ? { coverage } : undefined,
			};
		}),
	};
};

const productName = (id: string, catalog: ProductCatalog, fallback?: string) => {
	const product = catalog.find((item) => item._id === id);
	return fallback || product?.name || product?.commercialName || 'Produto';
};

const getSectorRecommendations = (sector: MaintenanceSectorFertilization) => {
	if (Array.isArray(sector.recommendations)) return sector.recommendations;
	return sector.recommendations ? [sector.recommendations] : [];
};

const recommendationsOfMonth = (sector: MaintenanceSectorFertilization, month: string) =>
	getSectorRecommendations(sector).filter(
		(recommendation) => recommendation.period && monthOf(recommendation.period) === month
	);

export const mapMixtureSystemRecommendation = (
	advice: FertilizationPlanAdvice,
	month: string,
	catalog: ProductCatalog
): PlanningTableData => {
	const sectors = getIrrigationRecommendations(advice);
	const area = sectors.reduce((total, sector) => total + (Number(sector.size) || 0), 0);
	const totals = new Map<string, { name: string; totalKg: number }>();

	sectors.forEach((sector) => {
		recommendationsOfMonth(sector, month).forEach((recommendation) => {
			(recommendation.proposal ?? []).forEach((item) => {
				const current = totals.get(item.id) ?? {
					name: productName(item.id, catalog, item.name),
					totalKg: 0,
				};
				current.totalKg += Number(item.dose || 0) * (Number(sector.size) || 0);
				totals.set(item.id, current);
			});
		});
	});

	const appliedKg = new Map<string, number>();
	getMonthMixtures(advice.mixtures, month)
		.filter((mixture) => mixture.status === 'confirmed')
		.forEach((mixture) => {
			mixture.products.forEach((product) => {
				const factor = MASS_TO_KG[product.unit];
				if (factor == null) return;
				appliedKg.set(product.id, (appliedKg.get(product.id) ?? 0) + product.dose * factor);
			});
		});

	const perHectare = (value: number) => (area > 0 ? value / area : 0);

	return {
		columns: [
			{ key: 'product', label: 'PRODUTO', emphasis: 'strong' },
			{ key: 'recommended', label: 'DOSE RECOMENDADA', align: 'right' },
			{ key: 'applied', label: 'DOSE APLICADA', align: 'right' },
			{ key: 'total', label: 'NECESSIDADE TOTAL', align: 'right' },
		],
		rows: [...totals.entries()].map(([id, item]) => ({
			id,
			cells: {
				product: item.name,
				recommended: `${formatMixtureDecimal(perHectare(item.totalKg))} kg/ha`,
				applied: `${formatMixtureDecimal(perHectare(appliedKg.get(id) ?? 0))} kg/ha`,
				total: `${formatMixtureDecimal(item.totalKg)} kg`,
			},
		})),
	};
};
